import { LayoutGrid, List, Calendar, CheckSquare } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { ROUTES } from "@/components/shared/constant";
import type { PortalViewType } from "@/store/slices/portalsSlice";

interface PortalTabDefinition {
  id: PortalViewType;
  label: string;
  icon: LucideIcon;
  route: string;
}

export const PORTAL_TAB_DEFINITIONS: Record<PortalViewType, PortalTabDefinition> = {
  list: {
    id: "list",
    label: "List",
    icon: List,
    route: ROUTES.PORTAL_LIST,
  },
  queues: {
    id: "queues",
    label: "Queues",
    icon: CheckSquare,
    route: ROUTES.PORTAL_QUEUES,
  },
  board: {
    id: "board",
    label: "Board",
    icon: LayoutGrid,
    route: ROUTES.PORTAL_BOARD,
  },

  calendar: {
    id: "calendar",
    label: "Calendar",
    icon: Calendar,
    route: ROUTES.PORTAL_CALENDAR,
  },
};

/** Ordered list of all available tab IDs. Board is work-type-specific and rendered separately. */
export const ALL_TAB_IDS: PortalViewType[] = ["list", "queues", "calendar", "board"];

/** The default tab that is always present and cannot be removed */
export const DEFAULT_TAB: PortalViewType = "list";

// ─── Work Type Tab Helpers ───

const WORK_TYPE_PREFIX = "wt:";

/** Format a work type slug for display: "service_request" → "Service Requests" (or "Service Request" with plural=false) */
export function formatWorkTypeLabel(wt: string, options: { plural?: boolean } = {}): string {
  const { plural = true } = options;
  const formatted = wt.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  if (!plural) return formatted;
  return formatted.endsWith("s") ? formatted : `${formatted}s`;
}

function isWorkTypeTab(tabId: string): boolean {
  return tabId.startsWith(WORK_TYPE_PREFIX);
}

function getWorkTypeFromTabId(tabId: string): string {
  return tabId.slice(WORK_TYPE_PREFIX.length);
}

function makeWorkTypeTabId(workType: string): string {
  return `${WORK_TYPE_PREFIX}${workType}`;
}

export function getWorkTypeTabLabel(workType: string): string {
  return formatWorkTypeLabel(workType);
}

export function isViewTab(tabId: string): tabId is PortalViewType {
  return tabId in PORTAL_TAB_DEFINITIONS;
}
